import { useCallback } from 'react';

import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
    currentPositionAtom,
    activeSegmentationsAtom,
    totalFramesAtom,
    MAX_PROGRESS,
    progressAtom,
    scrubberLockAtom,
} from '@store';
import { colors, LockIcon, OpenLockIcon, u } from '@ui';

import { getNextSegmentationFrame, getPrevSegmentationFrame } from './helpers';
import * as css from './PositionControls.css';

const FIRST_POSITION = 'P1';
const LAST_POSITION = 'P10';

export const PositionControls = () => {
    const setProgress = useSetAtom(progressAtom);
    const totalFrames = useAtomValue(totalFramesAtom);
    const activeSegmentationEntries = useAtomValue(activeSegmentationsAtom);
    const currentPosition = useAtomValue(currentPositionAtom);
    const [scrubberLock, setScrubberLock] = useAtom(scrubberLockAtom);
    const handleNextPosition = useCallback(() => {
        const segmentFrame = getNextSegmentationFrame(activeSegmentationEntries, currentPosition);
        setProgress((segmentFrame * MAX_PROGRESS) / totalFrames);
    }, [activeSegmentationEntries, currentPosition, setProgress, totalFrames]);

    const handlePrevPosition = useCallback(() => {
        const segmentFrame = getPrevSegmentationFrame(activeSegmentationEntries, currentPosition);
        setProgress((segmentFrame * MAX_PROGRESS) / totalFrames);
    }, [activeSegmentationEntries, currentPosition, setProgress, totalFrames]);

    const handleLockButtonClick = useCallback(() => {
        setScrubberLock(!scrubberLock);
    }, [setScrubberLock, scrubberLock]);

    return (
        <div className={css.outerPicker}>
            <div className={css.picker}>
                <button
                    className={css.prevPositionButton}
                    disabled={currentPosition === FIRST_POSITION}
                    onClick={handlePrevPosition}
                >
                    <CaretLeft
                        color={currentPosition !== FIRST_POSITION ? colors.bluegray[600] : colors.bluegray[300]}
                    />
                </button>
                <p className={css.currentPositionIndicator}>{currentPosition}</p>
                <button
                    className={css.nextPositionButton}
                    disabled={currentPosition === LAST_POSITION}
                    onClick={handleNextPosition}
                >
                    <CaretRight
                        color={currentPosition !== LAST_POSITION ? colors.bluegray[600] : colors.bluegray[300]}
                    />
                </button>
            </div>
            <button
                className={css.lockButton}
                onClick={handleLockButtonClick}
                aria-label={
                    scrubberLock ? `Unlock scrubber from ${scrubberLock}` : 'Lock scrubber to nearest swing position'
                }
            >
                {scrubberLock ? (
                    <LockIcon color={colors.blue[500]} weight="fill" size={u(2.5)} />
                ) : (
                    <OpenLockIcon color={colors.bluegray[500]} weight="fill" size={u(2.5)} />
                )}
            </button>
        </div>
    );
};
